/**
 * Rolling-window closed-trade evaluation (ported from python/app/summary).
 * Used client-side on KuCoin Live analytics when trades are already in memory.
 */

export type EvaluationCountPreset = 5 | 10 | 12 | 20 | 'all';

export interface TradeForEvaluation {
  symbol?: string;
  side?: string;
  pnl?: number | string | null;
  tradeFee?: number | string | null;
  fundingFee?: number | string | null;
  closeTime?: string | null;
  closeTimeMs?: number | string | null;
  closedAt?: string | null;
  timestamp?: string | null;
  exitReason?: string | null;
  entryPrice?: number | string | null;
  exitPrice?: number | string | null;
}

export type TradeDirection = 'LONG' | 'SHORT' | 'UNKNOWN';

export interface SideWinLossBreakdown {
  longWins: number;
  longLosses: number;
  shortWins: number;
  shortLosses: number;
}

export interface EvaluatedTradeRow {
  label: string;
  direction: TradeDirection;
  closedAt: Date;
  closedDisplay: string;
  /** Net realised P&L from KuCoin/DB (after fees). */
  pnlNet: number;
  /** Price-movement P&L before fees: pnlNet − fees (fees stored as negative costs). */
  pnlGross: number;
  fees: number;
}

export interface TradeEvaluationOptions {
  /** Strategy config fixedOrderAmount — used for return-on-order-size */
  fixedOrderAmount?: number;
}

export interface TradeWindowEvaluation {
  title: string;
  requestedCount: EvaluationCountPreset;
  actualCount: number;
  evaluateAll: boolean;
  trades: EvaluatedTradeRow[];
  grossPnl: number;
  totalFees: number;
  netPnl: number;
  winCount: number;
  lossCount: number;
  breakEvenCount: number;
  winRate: number;
  sideBreakdown: SideWinLossBreakdown;
  /** Net P&L as % of configured order size (null when order size unknown) */
  returnOnOrderSize: number | null;
  /** Order size used for returnOnOrderSize calculation */
  orderSize: number | null;
  largestWinners: EvaluatedTradeRow[];
  largestLosers: EvaluatedTradeRow[];
  observations: string[];
  suggestions: string[];
  totalTradesAvailable: number;
}

const DATE_DISPLAY: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

const DATE_SHORT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
};

function toNum(val: unknown, fallback = 0): number {
  const n = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(n) ? n : fallback;
}

function tradeCloseMs(trade: TradeForEvaluation): number {
  if (trade.closeTimeMs != null && trade.closeTimeMs !== '') {
    const ms = toNum(trade.closeTimeMs, NaN);
    if (Number.isFinite(ms)) return ms;
  }
  const raw = trade.closeTime || trade.closedAt || trade.timestamp;
  if (!raw) return 0;
  const t = parseTradeClosedDate(raw)?.getTime() ?? NaN;
  return Number.isFinite(t) ? t : 0;
}

/** Parse DB/KuCoin close timestamps; treat naive ISO/datetime strings as UTC. */
export function parseTradeClosedDate(raw: string | null | undefined): Date | null {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const ms = Number(s);
    return Number.isFinite(ms) ? new Date(ms) : null;
  }
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) {
    return new Date(s.replace(' ', 'T') + 'Z');
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) && !/[zZ]$/.test(s) && !/[+-]\d{2}:\d{2}$/.test(s)) {
    return new Date(s + 'Z');
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format closed time for trade tables (matches KuCoin Live display). */
export function formatTradeClosedDisplay(
  trade: TradeForEvaluation,
  options: Intl.DateTimeFormatOptions = DATE_DISPLAY
): string {
  const ms = tradeCloseMs(trade);
  if (!ms) return '—';
  try {
    const d = new Date(ms);
    return d.toLocaleDateString('en-GB', options)
      + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(trade.closeTime || trade.closedAt || trade.timestamp || '').slice(0, 16);
  }
}

function normalizeSide(side?: string): 'LONG' | 'SHORT' | 'UNKNOWN' {
  const s = (side || '').toUpperCase();
  if (s === 'LONG' || s === 'BUY') return 'LONG';
  if (s === 'SHORT' || s === 'SELL') return 'SHORT';
  return 'UNKNOWN';
}

export function tradeLabel(symbol?: string, direction?: TradeDirection | string): string {
  const base = (symbol || 'UNKNOWN')
    .replace(/USDTM$/i, '')
    .replace(/USDT$/i, '');
  const dir = typeof direction === 'string' && (direction === 'LONG' || direction === 'SHORT')
    ? direction
    : normalizeSide(direction);
  return dir === 'UNKNOWN' ? base : `${base} ${dir}`;
}

/** Infer LONG vs SHORT for CLOSE rows (DB rollback often has side=CLOSE only). */
export function inferTradeDirection(trade: TradeForEvaluation): TradeDirection {
  const fromSide = normalizeSide(trade.side);
  if (fromSide !== 'UNKNOWN') return fromSide;

  const er = (trade.exitReason || '').toUpperCase();
  if (er.includes('SHORT')) return 'SHORT';
  if (er.includes('LONG')) return 'LONG';

  const entry = toNum(trade.entryPrice, 0);
  const exit = toNum(trade.exitPrice, 0);
  const pnl = toNum(trade.pnl, 0);
  if (entry > 0 && exit > 0 && pnl !== 0) {
    const exitVsEntry = exit - entry;
    if (pnl > 0 && exitVsEntry > 0) return 'LONG';
    if (pnl > 0 && exitVsEntry < 0) return 'SHORT';
    if (pnl < 0 && exitVsEntry > 0) return 'SHORT';
    if (pnl < 0 && exitVsEntry < 0) return 'LONG';
  }

  return 'UNKNOWN';
}

/** Parse LONG/SHORT from API snapshot row (label or side field). */
export function directionFromSnapshotRow(
  label?: string,
  side?: string
): TradeDirection {
  const fromSide = normalizeSide(side);
  if (fromSide !== 'UNKNOWN') return fromSide;
  const l = (label || '').trim().toUpperCase();
  if (l.endsWith(' SHORT')) return 'SHORT';
  if (l.endsWith(' LONG')) return 'LONG';
  return 'UNKNOWN';
}

export function computeSideWinLossFromSnapshotTrades(
  trades: Array<{
    label?: string;
    side?: string;
    netPnl?: number | string | null;
    pnlGross?: number | string | null;
    pnlNet?: number | string | null;
  }>
): SideWinLossBreakdown {
  const rows: EvaluatedTradeRow[] = trades.map((t) => {
    const direction = directionFromSnapshotRow(t.label, t.side);
    const pnlNet = toNum(t.netPnl ?? t.pnlNet ?? t.pnlGross, 0);
    return {
      label: t.label || '',
      direction,
      closedAt: new Date(0),
      closedDisplay: '',
      pnlNet,
      pnlGross: pnlNet,
      fees: 0,
    };
  });
  return computeSideWinLossBreakdown(rows);
}

export function computeSideWinLossBreakdown(trades: EvaluatedTradeRow[]): SideWinLossBreakdown {
  const breakdown: SideWinLossBreakdown = {
    longWins: 0,
    longLosses: 0,
    shortWins: 0,
    shortLosses: 0,
  };
  for (const t of trades) {
    if (t.pnlNet > 0) {
      if (t.direction === 'LONG') breakdown.longWins += 1;
      else if (t.direction === 'SHORT') breakdown.shortWins += 1;
    } else if (t.pnlNet < 0) {
      if (t.direction === 'LONG') breakdown.longLosses += 1;
      else if (t.direction === 'SHORT') breakdown.shortLosses += 1;
    }
  }
  return breakdown;
}

export function formatPnlDisplay(amount: number): string {
  if (amount >= 0) {
    if (amount >= 0.01) return `+${amount.toFixed(2)}`;
    return `+${amount.toFixed(4)}`.replace(/\.?0+$/, '');
  }
  const a = Math.abs(amount);
  if (a >= 0.01) return `-${a.toFixed(2)}`;
  return `-${a.toFixed(4)}`.replace(/\.?0+$/, '');
}

export function formatMoneySigned(amount: number): string {
  const prefix = amount >= 0 ? '+' : '-';
  return `${prefix}$${Math.abs(amount).toFixed(2)}`;
}

export function formatPercentSigned(pct: number): string {
  const prefix = pct >= 0 ? '+' : '-';
  return `${prefix}${Math.abs(pct).toFixed(1)}%`;
}

function resolveOrderSize(fixedOrderAmount?: number): number | null {
  const n = toNum(fixedOrderAmount, NaN);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function computeReturnOnOrderSize(netPnl: number, orderSize: number | null): number | null {
  if (orderSize == null || orderSize <= 0) return null;
  return (netPnl / orderSize) * 100;
}

function formatClosedAt(d: Date): string {
  return d.toLocaleString('en-GB', DATE_DISPLAY);
}

function formatClosedShort(d: Date): string {
  return d.toLocaleString('en-GB', DATE_SHORT);
}

/** Fees are costs — store as negative so net = gross + fees. */
export function normalizeFeeAsCost(amount: number): number {
  if (amount === 0) return 0;
  return amount > 0 ? -Math.abs(amount) : amount;
}

function normalizeTrade(trade: TradeForEvaluation): EvaluatedTradeRow {
  const closedMs = tradeCloseMs(trade);
  const closedAt = closedMs > 0 ? new Date(closedMs) : new Date(0);
  const tradeFee = normalizeFeeAsCost(toNum(trade.tradeFee, 0));
  const fundingFee = normalizeFeeAsCost(toNum(trade.fundingFee, 0));
  const fees = tradeFee + fundingFee;
  const pnlNet = toNum(trade.pnl, 0);
  const direction = inferTradeDirection(trade);
  return {
    label: tradeLabel(trade.symbol, direction),
    direction,
    closedAt,
    closedDisplay: closedMs > 0 ? formatClosedAt(closedAt) : '—',
    pnlNet,
    pnlGross: pnlNet - fees,
    fees,
  };
}

function resolveCount(preset: EvaluationCountPreset, available: number): { count: number; evaluateAll: boolean } {
  if (preset === 'all') {
    return { count: available, evaluateAll: true };
  }
  return { count: Math.min(preset, available), evaluateAll: false };
}

function buildObservations(
  subset: EvaluatedTradeRow[],
  gross: number,
  fees: number,
  net: number,
  wins: number,
  losses: number,
  returnOnOrderSize: number | null,
  orderSize: number | null
): { observations: string[]; suggestions: string[] } {
  const observations: string[] = [];
  const suggestions: string[] = [];
  const winRate = subset.length ? (wins / subset.length) * 100 : 0;
  const feePct = gross > 0 ? (Math.abs(fees) / gross) * 100 : 0;

  const netWord = net > 0 ? 'profitable' : net < 0 ? 'unprofitable' : 'break-even';
  observations.push(
    `The strategy was ${netWord} over the last ${subset.length} trades, ` +
      `generating about $${Math.abs(net).toFixed(2)} net ${net >= 0 ? 'after' : 'loss after'} fees.`
  );

  if (returnOnOrderSize != null && orderSize != null) {
    observations.push(
      `Return on the configured $${orderSize.toFixed(2)} order size is ${formatPercentSigned(returnOnOrderSize)} ` +
        `over this ${subset.length}-trade window.`
    );
  }

  if (subset.length) {
    const best = subset.reduce((a, b) => (b.pnlNet > a.pnlNet ? b : a), subset[0]);
    if (best.pnlNet > 0 && net > 0 && best.pnlNet >= net * 0.5) {
      observations.push(
        `A single ${best.label} trade contributed more than half the net profit, ` +
          'indicating profits are currently concentrated in a few larger winners.'
      );
    }
  }

  if (gross > 0 && fees < 0) {
    observations.push(
      `Fees consumed about ${feePct.toFixed(0)}% of gross profits ` +
        `($${Math.abs(fees).toFixed(4)} of $${gross.toFixed(4)}), which is significant given the small position sizes.`
    );
  }

  const longs = subset.filter((t) => t.direction === 'LONG');
  const shorts = subset.filter((t) => t.direction === 'SHORT');
  const longPnl = longs.reduce((s, t) => s + t.pnlNet, 0);
  const shortPnl = shorts.reduce((s, t) => s + t.pnlNet, 0);
  if (longs.length && shorts.length) {
    if (shortPnl > longPnl) {
      observations.push('Short positions performed noticeably better than longs in this sample.');
    } else if (longPnl > shortPnl) {
      observations.push('Long positions performed noticeably better than shorts in this sample.');
    }
  }

  const newest = subset[0];
  if (newest && newest.pnlNet < 0) {
    const priorWins = subset.slice(1).filter((t) => t.pnlNet > 0).length;
    if (priorWins >= 3) {
      observations.push(
        `The most recent ${newest.label} (${formatPnlDisplay(newest.pnlNet)}) ` +
          'erased a large portion of the gains from several preceding winning trades, ' +
          'suggesting stop-loss sizing may be too large relative to average winners.'
      );
    }
  }

  suggestions.push(
    `For these ${subset.length} trades, your bot is showing the classic pattern of a trend-following system:`
  );
  suggestions.push(`Win rate around ${winRate.toFixed(0)}%.`);
  suggestions.push('Several small wins.');
  suggestions.push('A few larger losses.');
  if (net > 0) {
    suggestions.push('Still profitable overall because the winners slightly outweigh the losers.');
  } else {
    suggestions.push('Currently underwater because losses outweigh winners in this window.');
  }
  suggestions.push(
    'The key metric to monitor is whether future losses remain capped. ' +
      'If losses like large negative trades become more frequent, they can quickly overwhelm the current edge. ' +
      'Conversely, if you can reduce average losing trade size while keeping winners unchanged, ' +
      'profitability should improve significantly.'
  );

  return { observations, suggestions };
}

/**
 * Sort trades newest-first, take the first `count`, and compute window stats.
 */
export function computeTradeWindowEvaluation(
  rawTrades: TradeForEvaluation[],
  preset: EvaluationCountPreset = 12,
  options: TradeEvaluationOptions = {}
): TradeWindowEvaluation | null {
  if (!rawTrades.length) return null;

  const sorted = [...rawTrades]
    .map(normalizeTrade)
    .filter((t) => t.closedAt.getTime() > 0)
    .sort((a, b) => b.closedAt.getTime() - a.closedAt.getTime());

  const withFallback = sorted.length
    ? sorted
    : [...rawTrades].map(normalizeTrade).sort((a, b) => b.closedAt.getTime() - a.closedAt.getTime());

  const { count, evaluateAll } = resolveCount(preset, withFallback.length);
  const subset = withFallback.slice(0, count);

  const netPnl = subset.reduce((s, t) => s + t.pnlNet, 0);
  const totalFees = subset.reduce((s, t) => s + t.fees, 0);
  const grossPnl = netPnl - totalFees;

  const winCount = subset.filter((t) => t.pnlNet > 0).length;
  const lossCount = subset.filter((t) => t.pnlNet < 0).length;
  const breakEvenCount = subset.length - winCount - lossCount;
  const winRate = subset.length ? (winCount / subset.length) * 100 : 0;
  const sideBreakdown = computeSideWinLossBreakdown(subset);
  const orderSize = resolveOrderSize(options.fixedOrderAmount);
  const returnOnOrderSize = computeReturnOnOrderSize(netPnl, orderSize);

  const largestWinners = [...subset]
    .filter((t) => t.pnlNet > 0)
    .sort((a, b) => b.pnlNet - a.pnlNet)
    .slice(0, 3);

  const largestLosers = [...subset]
    .filter((t) => t.pnlNet < 0)
    .sort((a, b) => a.pnlNet - b.pnlNet)
    .slice(0, 3);

  const { observations, suggestions } = buildObservations(
    subset,
    grossPnl,
    totalFees,
    netPnl,
    winCount,
    lossCount,
    returnOnOrderSize,
    orderSize
  );

  const title = evaluateAll
    ? `All ${subset.length} Closed Trades Evaluation`
    : `Last ${subset.length} Closed Trades Evaluation`;

  return {
    title,
    requestedCount: preset,
    actualCount: subset.length,
    evaluateAll,
    trades: subset,
    grossPnl,
    totalFees,
    netPnl,
    winCount,
    lossCount,
    breakEvenCount,
    winRate,
    sideBreakdown,
    returnOnOrderSize,
    orderSize,
    largestWinners,
    largestLosers,
    observations,
    suggestions,
    totalTradesAvailable: withFallback.length,
  };
}

export function formatEvaluationReport(eval_: TradeWindowEvaluation): string {
  const lines: string[] = [eval_.title, '', 'Date/Time\tTrade\tP&L ($)', ''];
  for (const t of eval_.trades) {
    lines.push(`${t.closedDisplay}\t${t.label}\t${formatPnlDisplay(t.pnlNet)}`);
  }
  lines.push(
    '',
    'Summary',
    '',
    `Gross P&L (before fees): ${formatMoneySigned(eval_.grossPnl)}`,
    `Total fees: ${formatMoneySigned(eval_.totalFees)}`,
    `Net P&L (after fees): ${formatMoneySigned(eval_.netPnl)}`,
    '',
    `Winning trades: ${eval_.winCount}`,
    `Losing trades: ${eval_.lossCount}`,
    `Win rate: ${eval_.winRate.toFixed(1)}%`,
    '',
    `LONG wins / losses: ${eval_.sideBreakdown.longWins} / ${eval_.sideBreakdown.longLosses}`,
    `SHORT wins / losses: ${eval_.sideBreakdown.shortWins} / ${eval_.sideBreakdown.shortLosses}`
  );
  if (eval_.returnOnOrderSize != null && eval_.orderSize != null) {
    lines.push(
      `Return on order size ($${eval_.orderSize.toFixed(2)}): ${formatPercentSigned(eval_.returnOnOrderSize)}`
    );
  }
  return lines.join('\n');
}

/** Short date for winner/loser lines */
export function formatTradeMoment(row: EvaluatedTradeRow): string {
  return row.closedAt.getTime() > 0 ? formatClosedShort(row.closedAt) : '—';
}

function isDbClosedTrade(trade: Record<string, unknown>): boolean {
  if (trade.closedAt || trade.exitReason) return true;
  const side = String(trade.side || '').toUpperCase();
  return side === 'CLOSE';
}

/** Normalize DB rollback trades for the evaluation panel (closed positions only). */
export function mapDbTradesForEvaluation(rawTrades: TradeForEvaluation[] | Record<string, unknown>[]): TradeForEvaluation[] {
  return (rawTrades as Record<string, unknown>[])
    .filter(isDbClosedTrade)
    .map((t) => {
      const tradeFee = normalizeFeeAsCost(toNum(t.fees, 0));
      const closeTime = (t.closedAt || t.closeTime || t.timestamp) as string | null | undefined;
      const draft: TradeForEvaluation = {
        symbol: t.symbol as string | undefined,
        side: t.side as string | undefined,
        pnl: t.pnl as number | string | null | undefined,
        tradeFee,
        fundingFee: 0,
        closeTime,
        closedAt: closeTime,
        timestamp: t.timestamp as string | null | undefined,
        exitReason: t.exitReason as string | null | undefined,
        entryPrice: (t.entryPrice ?? t.openPrice) as number | string | null | undefined,
        exitPrice: (t.price ?? t.closePrice ?? t.exitPrice) as number | string | null | undefined,
      };
      const direction = inferTradeDirection(draft);
      return {
        ...draft,
        side: direction !== 'UNKNOWN' ? direction : draft.side,
      };
    });
}

/** Extract fixedOrderAmount from strategy config JSON (string or object). */
export function parseFixedOrderAmount(config: unknown): number | undefined {
  if (config == null || config === '') return undefined;
  try {
    const parsed = typeof config === 'string' ? JSON.parse(config) : config;
    const n = Number((parsed as { fixedOrderAmount?: unknown }).fixedOrderAmount);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  } catch {
    return undefined;
  }
}

/** Pick trades for evaluation based on data source. */
export function mapTradesForEvaluation(
  rawTrades: TradeForEvaluation[] | Record<string, unknown>[],
  source: 'kucoin' | 'db'
): TradeForEvaluation[] {
  if (source === 'db') return mapDbTradesForEvaluation(rawTrades);
  return (rawTrades as Record<string, unknown>[]).map((t) => {
    const draft: TradeForEvaluation = {
      symbol: t.symbol as string | undefined,
      side: t.side as string | undefined,
      pnl: t.pnl as number | string | null | undefined,
      tradeFee: t.tradeFee as number | string | null | undefined,
      fundingFee: t.fundingFee as number | string | null | undefined,
      closeTime: (t.closeTime || t.closedAt) as string | null | undefined,
      closeTimeMs: t.closeTimeMs as number | string | null | undefined,
      closedAt: (t.closedAt || t.closeTime) as string | null | undefined,
      entryPrice: (t.entryPrice ?? t.openPrice) as number | string | null | undefined,
      exitPrice: (t.price ?? t.closePrice) as number | string | null | undefined,
      exitReason: t.exitReason as string | null | undefined,
    };
    const direction = inferTradeDirection(draft);
    return {
      ...draft,
      side: direction !== 'UNKNOWN' ? direction : draft.side,
    };
  });
}
